"use client";

import React, { useEffect, useMemo, useState } from "react";
import TinderCard from "react-tinder-card";
import type { SwipeDirection } from "../types";
import { getRingImageUrls } from "../constants";

export type RingLike = {
  id: string;
  url: string | string[];
  optimizedImages?: {
    original: string;
    variants: Record<string, string>;
    createdAt?: number;
  }[];
  salesUrl?: string;
  salesPrice?: number;
  currency?: string;
  uploadedBy?: unknown;
};

export type RingSwipeDeckProps<T extends RingLike = RingLike> = {
  cards: T[];
  onCardLeftScreen?: (direction: SwipeDirection, ring: T) => void;
  onMoreInfo?: (ring: T) => void;
  onVisitStore?: (ring: T) => void;
  renderTagIcon?: () => React.ReactNode;
  renderLeftArrowIcon?: () => React.ReactNode;
  renderRightArrowIcon?: () => React.ReactNode;
  renderLikeIcon?: () => React.ReactNode;
  renderDislikeIcon?: () => React.ReactNode;
  visitStoreLabel?: string;
  renderCardOverlay?: (ring: T, index: number) => React.ReactNode;
  cardClassName?: string;
  emptyState?: React.ReactNode;
  collectionMode?: boolean;
  disabled?: boolean;
  enableKeyboard?: boolean;
  moreInfoLabel?: string;
  className?: string;
};

const defaultMoreInfoLabel = "More info";

const RingSwipeDeck = <T extends RingLike>({
  cards,
  onCardLeftScreen,
  onMoreInfo,
  onVisitStore,
  renderTagIcon,
  renderLeftArrowIcon,
  renderRightArrowIcon,
  renderLikeIcon,
  renderDislikeIcon,
  visitStoreLabel = "Visit store",
  renderCardOverlay,
  cardClassName,
  emptyState,
  collectionMode = false,
  disabled = false,
  enableKeyboard = true,
  moreInfoLabel = defaultMoreInfoLabel,
  className,
}: RingSwipeDeckProps<T>) => {
  const [imageNumber, setImageNumber] = useState(0);
  const [hasBounced, setHasBounced] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [vh, setVh] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight * 0.01,
  );
  const activeRingId = cards[cards.length - 1]?.id;

  const childRefs = useMemo(
    () =>
      Array(cards.length)
        .fill(0)
        .map(() =>
          React.createRef<{
            swipe: (dir: SwipeDirection) => Promise<void>;
            restoreCard: () => Promise<void>;
          }>(),
        ),
    [cards.length],
  );

  const handleSwipe = (direction: SwipeDirection, ringId: string) => {
    if (disabled) return;
    const cardsLeft = cards;
    if (cardsLeft.length) {
      const toBeRemoved = cardsLeft.find((ring) => ring.id === ringId);
      if (!toBeRemoved) return;
      const index = cards.indexOf(toBeRemoved);
      if (index === -1) return;
      childRefs[index]?.current?.swipe(direction);
    }
  };

  useEffect(() => {
    if (!enableKeyboard || disabled) return;
    const arrowKeySwipe = (direction: SwipeDirection) => {
      if (!cards.length) return;
      const toBeRemoved = cards[cards.length - 1];
      const index = cards.indexOf(toBeRemoved);
      if (index !== -1) childRefs[index]?.current?.swipe(direction);
    };

    const keySwipe = (event: KeyboardEvent) => {
      const { key } = event;
      switch (key) {
        case "ArrowLeft":
          arrowKeySwipe("left");
          break;
        case "ArrowRight":
          arrowKeySwipe("right");
          break;
        case "ArrowDown":
          arrowKeySwipe("down");
          break;
        case "ArrowUp":
          arrowKeySwipe("up");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", keySwipe);
    return () => {
      window.removeEventListener("keydown", keySwipe);
    };
  }, [cards, childRefs, disabled, enableKeyboard]);

  useEffect(() => {
    setImageNumber(0);
    setHasBounced(false);
  }, [activeRingId]);

  useEffect(() => {
    const handleResize = () => {
      const newVh = window.innerHeight * 0.01;
      setVh(newVh);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const delay = (ms: number) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const imageNumberForRingWithId = (id: string) => {
    const currentRing = cards[cards.length - 1];
    if (!currentRing || currentRing.id !== id) return 0;
    return imageNumber;
  };

  const getUrlsForRing = (ring: T) => getRingImageUrls(ring);

  const isActive = (index: number) => (index === cards.length - 1 ? "active" : "");

  const defaultTagIcon = () => <span className="tag-icon pressable">$</span>;
  const defaultLeftArrow = () => <span className="arrow-icon pressable">◀</span>;
  const defaultRightArrow = () => <span className="arrow-icon pressable">▶</span>;
  const defaultDislike = () => <span className="cross-icon pressable">✕</span>;
  const defaultLike = () => <span className="heart-icon pressable">❤</span>;

  if (!cards.length && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <div className={className}>
      <div className="swipe-container">
        <div className="card-container" style={{ "--vh": `${vh}px` } as React.CSSProperties}>
          {cards.map((ring, index) => (
            <TinderCard
              ref={childRefs[index]}
              key={ring.id}
              onCardLeftScreen={(direction) =>
                onCardLeftScreen?.(direction as SwipeDirection, ring)
              }
              preventSwipe={disabled ? ["left", "right", "up", "down"] : undefined}
            >
              <div className="swipe">
                <div
                  style={
                    {
                      backgroundImage: `url(${getUrlsForRing(ring)[imageNumberForRingWithId(ring.id)] || getUrlsForRing(ring)[0] || ""})`,
                      "--vh": `${vh}px`,
                    } as React.CSSProperties
                  }
                  className={`card ${cardClassName ?? ""} ${collectionMode ? "collection" : ""}`.trim()}
                >
                  {ring.salesUrl && onVisitStore ? (
                    <button
                      onMouseEnter={async () => {
                        await delay(200);
                        setIsHover(true);
                      }}
                      onMouseLeave={() => setIsHover(false)}
                      onClick={() => onVisitStore(ring)}
                      className="card__sales-tag pressable"
                      type="button"
                    >
                      {renderTagIcon ? renderTagIcon() : defaultTagIcon()}
                      <span className={`card__hover-text ${isHover ? "visible" : ""}`}>
                        {visitStoreLabel}
                      </span>
                    </button>
                  ) : null}

                  {getUrlsForRing(ring).length > 1 && imageNumber > 0 ? (
                    <button
                      onClick={() => setImageNumber(imageNumber - 1)}
                      className="previous-image pressable"
                      type="button"
                    >
                      {renderLeftArrowIcon ? renderLeftArrowIcon() : defaultLeftArrow()}
                    </button>
                  ) : null}

                  <button
                    onClick={() => handleSwipe("left", ring.id)}
                    className="card__dislike pressable"
                    type="button"
                  >
                    {renderDislikeIcon ? renderDislikeIcon() : defaultDislike()}
                  </button>
                  <button
                    onClick={() => onMoreInfo?.(ring)}
                    className="card__info pressable"
                    type="button"
                  >
                    {moreInfoLabel}
                  </button>
                  <button
                    onClick={() => handleSwipe("right", ring.id)}
                    className="card__like pressable"
                    type="button"
                  >
                    {renderLikeIcon ? renderLikeIcon() : defaultLike()}
                  </button>

                  {getUrlsForRing(ring).length - 1 > imageNumber ? (
                    <button
                      onClick={() => setImageNumber(imageNumber + 1)}
                      className={`next-image pressable ${
                        !hasBounced ? "bounce " + isActive(index) : ""
                      }`}
                      onAnimationEnd={() => setHasBounced(true)}
                      type="button"
                    >
                      {renderRightArrowIcon ? (
                        renderRightArrowIcon()
                      ) : (
                        <span
                          className={`arrow-icon pressable ${
                            !hasBounced ? "bounce " + isActive(index) : ""
                          }`}
                          onAnimationEnd={() => setHasBounced(true)}
                        >
                          ▶
                        </span>
                      )}
                    </button>
                  ) : null}

                  {renderCardOverlay ? renderCardOverlay(ring, index) : null}
                </div>
              </div>
            </TinderCard>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RingSwipeDeck;
